import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import Modal from 'react-bootstrap/Modal';
import { Link } from 'react-router-dom';
import DeleteAlert from '../../Common/Layout/DeleteAlert';

const initialFieldValues = {
    branchId: "00000000-0000-0000-0000-000000000000",
    companyId: "00000000-0000-0000-0000-000000000000",
    branchName: "",
    branchType: "BRANCH",
};

const CompanyBranches = () => {
    const { user } = useAuth();
    // State Management
    const [listLoading, setListLoading] = useState(true);
    const [btnLoading, setBtnLoading] = useState(false);
    const [apiError, setApiError] = useState('');
    const [branches, setBranches] = useState([]);
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({});

    // Pagination State
    const [pageSize, setPageSize] = useState(20);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    // Modal State
    const [addShow, setAddShow] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Central data fetching function
    const GetData = async (currentPage) => {
        if (!user?.cid) {
            setListLoading(false);
            return;
        }
        setListLoading(true);
        setApiError('');
        try {
            const res = await apiService.get(`${config.GETBRANCHBYCOMPANY}/${user.cid}`, pageSize, currentPage);

            setBranches(res.data || []);
            setTotalPages(res.totalPages || 0);
            setTotalRecords(res.totalRecords || 0);
            setPageNumber(res.pageNumber || currentPage);
            setPageSize(res.pageSize || 20);

        } catch (err) {
            setApiError("Failed to fetch branches. Please try again.");
            setBranches([]);
        } finally {
            setListLoading(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        if (user?.cid) {
            GetData(1);
        }
    }, [user, pageSize]); // Re-fetch if user or pageSize changes

    // Form input handler
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const fieldsToUppercase = ["branchName"];
        setValues({
            ...values,
            [name]: fieldsToUppercase.includes(name) ? value.toUpperCase() : value
        });
        if (errors[name]) {
            setErrors({ ...errors, [name]: true });
        }
    };

    // Form validation
    const validate = () => {
        let tempErrors = {};
        tempErrors.branchName = values.branchName ? true : false;
        setErrors(tempErrors);
        return Object.values(tempErrors).every(x => x === true);
    };

    // Reset form state
    const resetForm = () => {
        setValues(initialFieldValues);
        setErrors({});
        setBtnLoading(false);
    };

    // Handle create/update submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setBtnLoading(true);
            setApiError('');
            const formData = { ...values, companyId: user?.cid };

            try {
                const isNew = formData.branchId === "00000000-0000-0000-0000-000000000000";
                if (isNew) {
                    await apiService.create(config.CREATEBRANCH, formData);
                } else {
                    await apiService.update(config.UPDATEBRANCH, formData);
                }

                await GetData(pageNumber); // Refresh current page
                handleAddClose();

            } catch (err) {
                setApiError("Failed to create or update branch.");
            } finally {
                setBtnLoading(false);
            }
        }
    };

    // Handle deletion
    const handleDelete = async () => {
        if (itemToDelete !== null) {
            setApiError('');
            try {
                await apiService.remove(config.DELETEBRANCH, itemToDelete);
                // After deleting, if it was the last item on a page, go to the previous page
                const newPageNumber = branches.length === 1 && pageNumber > 1 ? pageNumber - 1 : pageNumber;
                await GetData(newPageNumber);
            } catch (error) {
                setApiError("Failed to delete branch.");
                console.error("Delete error:", error);
            }
            handleCloseDeleteModal();
        }
    };

    // Modal controls
    const handleAddClose = () => setAddShow(false);
    const handleAddShow = () => {
        resetForm();
        setAddShow(true);
    };
    const showEditDetails = (branch) => {
        setValues(branch);
        setAddShow(true);
    };
    const handleOpenDeleteModal = (id) => {
        setItemToDelete(id);
        setShowDeleteModal(true);
    };
    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setItemToDelete(null);
    };

    // Pagination controls
    const GetPageData = (number) => {
        if (pageNumber !== number) {
            GetData(number);
        }
    }

    // --- IMPROVEMENT: Added 'active' class for the current page number ---
    const renderPageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
        <li className={`page-item ${pageNumber === number ? 'active' : ''}`} key={number}>
            <Link to="#" className="page-link" onClick={() => GetPageData(number)}>
                {number}
            </Link>
        </li>
    ));

    const applyErrorClass = (field) => (field in errors && errors[field] === false) ? "is-invalid" : "";

    return (
        <div className="card p-2">
            <div className="me-2 pb-1">
                <div className="row g-4 align-items-center">
                    <div className="col-sm-auto ms-auto">
                        <div className="hstack gap-2">
                            <button type="button" className="btn btn-primary add-btn" onClick={handleAddShow} ><i className="ri-add-line align-bottom me-1" /> Add Branch</button>
                        </div>
                    </div>
                </div>
            </div>

            {apiError && <div className="alert alert-danger alert-dismissible fade show"><button type="button" className="btn-close" onClick={() => setApiError('')}></button>{apiError}</div>}

            {listLoading ? (
                <div className="text-center p-5"><div className="spinner-border text-primary" /></div>
            ) : (
                <>
                    {branches.length > 0 ? (
                        <>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>S.No</th>
                                            <th>Branch Name</th>
                                            <th>Branch Type</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {branches.map((branch, index) => (
                                            <tr key={branch.branchId}>
                                                {/* --- CRITICAL FIX: Correct Serial Number Calculation --- */}
                                                <td>{((pageNumber - 1) * pageSize) + index + 1}</td>
                                                <td>{branch.branchName}</td>
                                                <td><span className="badge bg-secondary-subtle text-secondary">{branch.branchType}</span></td>
                                                <td className="text-end">
                                                    <div className="d-flex gap-2 justify-content-end">
                                                        <Link to="#" className="btn btn-sm btn-soft-primary" onClick={() => showEditDetails(branch)} title="Edit">
                                                            <i className="ri-pencil-fill align-bottom" />
                                                        </Link>
                                                        <Link to="#" className="btn btn-sm btn-soft-danger" onClick={() => handleOpenDeleteModal(branch.branchId)} title="Delete">
                                                            <i className="ri-delete-bin-fill align-bottom" />
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                                <div className="flex-shrink-0">
                                    <div className="text-muted">
                                        Showing <span className="fw-semibold">{branches.length}</span> of <span className="fw-semibold">{totalRecords}</span> Results
                                    </div>
                                </div>
                                <ul className="pagination pagination-separated pagination-sm mb-0">
                                    <li className={`page-item ${pageNumber === 1 ? 'disabled' : ''}`}>
                                        <Link to="#" className="page-link" onClick={() => GetPageData(pageNumber - 1)}>Previous</Link>
                                    </li>
                                    {renderPageNumbers}
                                    <li className={`page-item ${pageNumber >= totalPages ? 'disabled' : ''}`}>
                                        <Link to="#" className="page-link" onClick={() => GetPageData(pageNumber + 1)}>Next</Link>
                                    </li>
                                </ul>
                            </div>
                        </>
                    ) : (
                        <div className="text-center p-5 border rounded">
                            <p className="mb-0 text-muted">You have not added any branches yet.</p>
                        </div>
                    )}
                </>
            )}

            {/* Modals remain unchanged as they are perfect */}
            <Modal show={addShow} onHide={handleAddClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{values.branchId === "00000000-0000-0000-0000-000000000000" ? "Add New" : "Edit"} Branch</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="mb-3">
                            <label htmlFor="branchName" className="form-label">Branch Name</label>
                            <input type="text" name="branchName" value={values.branchName} onChange={handleInputChange} className={`form-control ${applyErrorClass('branchName')}`} placeholder="e.g., Downtown Office" required />
                            {errors.branchName === false && <div className="invalid-feedback">Please enter a branch name.</div>}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="branchType" className="form-label">Branch Type</label>
                            <select name="branchType" value={values.branchType} onChange={handleInputChange} className="form-select">
                                <option value="BRANCH">BRANCH</option>
                                <option value="MAIN">MAIN</option>
                            </select>
                        </div>
                        <div className="hstack gap-2 justify-content-end mt-4">
                            <button type="button" className="btn btn-light" onClick={handleAddClose}>Cancel</button>
                            <button type="submit" className="btn btn-success" disabled={btnLoading}>
                                {btnLoading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            <DeleteAlert show={showDeleteModal} handleClose={handleCloseDeleteModal} handleDelete={handleDelete} />
        </div>
    );
};

export default CompanyBranches;